import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Play, Pause, Square, Loader2, Headphones } from "lucide-react";
import { fetchNarration, SPEED_OPTIONS } from "../lib/voice";
import { usePreferences } from "../context/PreferencesContext";
import { useAudio } from "../context/AudioContext";

// A discreet, editorial "Listen" control. Reusable across chat replies and articles.
// Narration language follows the active i18n locale, so a Portuguese page is
// read in Portuguese and a Spanish page in Spanish. ElevenLabs' multilingual
// v2 model handles all three site languages natively.
export const ListenButton = ({ text, idleKey = "listen.listen", testId = "listen-button", compact = false }) => {
  const { t, i18n } = useTranslation();
  const narrationLang = ((i18n.language || "en").split("-")[0]);
  const { prefs, setPref } = usePreferences();
  const { duck, unduck, narrationVolume } = useAudio();
  // Narration is a deliberate user action. It must play audibly even when
  // the ambient soundscape is muted — the ambient mute is for background
  // audio, not for the article reader. Clamp the effective volume so a
  // muted site never silently swallows the narration the visitor asked for.
  const effectiveNarrationVolume = () => Math.max(0.85, narrationVolume || 0);
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const chunksRef = useRef([]);
  const idxRef = useRef(0);
  const stoppedRef = useRef(false);
  const nextUrlRef = useRef(null);
  const duckedRef = useRef(false);
  const narrationVolRef = useRef(effectiveNarrationVolume());
  const [status, setStatus] = useState("idle"); // idle | loading | playing | paused | error
  const [progress, setProgress] = useState(0); // 0..1

  // Narration always respects the master volume / mute (updated live while playing).
  useEffect(() => {
    narrationVolRef.current = effectiveNarrationVolume();
    if (audioRef.current) audioRef.current.volume = narrationVolRef.current;
  }, [narrationVolume]);

  // Duck the ambient soundscape only while the voice is actively speaking.
  const setDuck = (v) => {
    if (v && !duckedRef.current) { duck(); duckedRef.current = true; }
    if (!v && duckedRef.current) { unduck(); duckedRef.current = false; }
  };

  useEffect(() => {
    // Cleanup on unmount.
    return () => {
      stoppedRef.current = true;
      if (audioRef.current) audioRef.current.pause();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      setDuck(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Split long narration into sentence-bounded chunks so the full article can be
  // read start-to-finish (a single large TTS request is too slow / times out).
  const splitIntoChunks = (input, max = 600) => {
    const sentences = input.match(/[^.!?\n]+[.!?]*\s*/g) || [input];
    const chunks = [];
    let cur = "";
    for (const s of sentences) {
      if ((cur + s).length > max && cur) { chunks.push(cur.trim()); cur = s; }
      else cur += s;
    }
    if (cur.trim()) chunks.push(cur.trim());
    return chunks.length ? chunks : [input];
  };

  const applySpeed = () => {
    if (audioRef.current) audioRef.current.playbackRate = prefs.playbackSpeed || 1;
  };

  const playChunk = async (i) => {
    if (stoppedRef.current) return;
    const total = chunksRef.current.length;
    if (i >= total) { setStatus("idle"); setProgress(0); idxRef.current = 0; nextUrlRef.current = null; setDuck(false); return; }
    idxRef.current = i;
    let url;
    try {
      url = await (nextUrlRef.current || fetchNarration(chunksRef.current[i], prefs.voice, narrationLang));
    } catch (e) {
      console.error("Narration error:", e);
      setStatus("error");
      setDuck(false);
      return;
    }
    if (stoppedRef.current || !url) return;
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.playbackRate = prefs.playbackSpeed || 1;
    audio.volume = narrationVolRef.current;
    // Prefetch the next chunk while this one plays.
    nextUrlRef.current = i + 1 < total ? fetchNarration(chunksRef.current[i + 1], prefs.voice, narrationLang).catch(() => null) : null;
    audio.ontimeupdate = () => {
      const intra = audio.duration ? audio.currentTime / audio.duration : 0;
      setProgress((i + Math.min(intra, 1)) / total);
    };
    audio.onended = () => { playChunk(i + 1); };
    audio.onerror = () => { setStatus("error"); setDuck(false); };
    try { await audio.play(); setStatus("playing"); } catch (e) { setStatus("error"); setDuck(false); }
  };

  const handlePlay = async () => {
    if (status === "playing") {
      audioRef.current?.pause();
      setStatus("paused");
      setDuck(false);
      return;
    }
    if (status === "paused" && audioRef.current) {
      applySpeed();
      audioRef.current.volume = narrationVolRef.current;
      audioRef.current.play();
      setStatus("playing");
      setDuck(true);
      return;
    }
    // Fresh narration
    stoppedRef.current = false;
    chunksRef.current = splitIntoChunks((text || "").trim());
    idxRef.current = 0;
    nextUrlRef.current = null;
    setStatus("loading");
    setProgress(0);
    setDuck(true);
    await playChunk(0);
  };

  const handleStop = () => {
    stoppedRef.current = true;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    nextUrlRef.current = null;
    idxRef.current = 0;
    setStatus("idle");
    setProgress(0);
    setDuck(false);
  };

  const cycleSpeed = () => {
    const idx = SPEED_OPTIONS.indexOf(prefs.playbackSpeed || 1);
    const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
    setPref("playbackSpeed", next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const active = status === "playing" || status === "paused" || status === "loading";

  return (
    <div className="inline-flex flex-col gap-1.5" data-testid={`${testId}-wrap`}>
      <div className="inline-flex items-center gap-2">
      <button
        onClick={handlePlay}
        data-testid={testId}
        disabled={status === "loading"}
        className={`group inline-flex items-center gap-2 border border-subtle-custom px-3 py-1.5 text-[9px] font-sans tracking-[0.2em] uppercase transition-all duration-300 rounded-none ${
          active
            ? "bg-[var(--accent-bronze)] text-white border-[var(--accent-bronze)]"
            : "bg-surface-custom text-secondary-custom hover:text-primary-custom hover:border-[var(--accent-bronze)]"
        }`}
      >
        {status === "loading" ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
        ) : status === "playing" ? (
          <Pause className="w-3.5 h-3.5" strokeWidth={1.5} />
        ) : status === "paused" ? (
          <Play className="w-3.5 h-3.5" strokeWidth={1.5} />
        ) : status === "error" ? (
          <Headphones className="w-3.5 h-3.5" strokeWidth={1.5} />
        ) : (
          <Headphones className="w-3.5 h-3.5" strokeWidth={1.5} />
        )}
        {!compact && (
          <span>
            {status === "loading"
              ? t("listen.preparing")
              : status === "playing"
              ? t("listen.pause")
              : status === "paused"
              ? t("listen.resume")
              : status === "error"
              ? t("listen.tryAgain")
              : t(idleKey)}
          </span>
        )}
      </button>

      {active && (
        <>
          <button
            onClick={handleStop}
            data-testid={`${testId}-stop`}
            className="inline-flex items-center justify-center border border-subtle-custom p-1.5 text-secondary-custom hover:text-primary-custom hover:border-[var(--accent-bronze)] transition-all duration-300"
            aria-label="Stop"
          >
            <Square className="w-3 h-3" strokeWidth={1.5} />
          </button>
          <button
            onClick={cycleSpeed}
            data-testid={`${testId}-speed`}
            className="inline-flex items-center justify-center border border-subtle-custom px-2 py-1.5 text-[9px] font-sans tracking-[0.15em] text-secondary-custom hover:text-primary-custom hover:border-[var(--accent-bronze)] transition-all duration-300"
            aria-label="Playback speed"
          >
            {prefs.playbackSpeed || 1}×
          </button>
        </>
      )}
      </div>

      {active && (
        <div
          className="h-0.5 w-full bg-subtle-custom overflow-hidden rounded-full"
          data-testid={`${testId}-progress`}
          role="progressbar"
          aria-valuenow={Math.round(progress * 100)}
        >
          <div
            className="h-full bg-[var(--accent-bronze)] transition-[width] duration-200 ease-linear"
            style={{ width: `${Math.max(2, progress * 100)}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default ListenButton;
